import { fetchConfig, json } from "@theme/fetch";
import { classes, debounce } from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";

const MAX_LENGTH = 200;

class CartGiftNote extends HTMLCustomElement {
  requiredRefs = ["toggle", "toggleLabel", "panel", "input", "count"];

  #saveDebounced = debounce(() => this.#save(), 300);

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.#syncCount();
    this.#setOpen(false);

    this.on("input", this.refs.input, this.#onInput);
    this.on("click", this.refs.toggle, this.#onToggle);
    this.on("click", this.refs.clear, this.#onClear);
    this.on("click", this.refs.submit, this.#onSubmit);
    this.on("keydown", this, this.#onKeydown);
  }

  get #content() {
    return this.closest(".cart-summary__content");
  }

  #onInput = () => {
    this.#syncCount();
    this.#saveDebounced();
  };

  /**
   * Opens or closes the overlay.
   * @param {MouseEvent} event
   */
  #onToggle = (event) => {
    event.preventDefault();
    this.#setOpen(!this.classList.contains(classes.open));
  };

  /**
   * Clears the gift note field.
   * @param {MouseEvent} event
   */
  #onClear = (event) => {
    event.preventDefault();
    this.refs.input.value = "";
    this.#syncCount();
    this.refs.input.focus();
    this.#save();
  };

  /**
   * Persists the note and closes the overlay.
   * @param {MouseEvent} event
   */
  #onSubmit = async (event) => {
    event.preventDefault();

    if (this.refs.submit) this.refs.submit.disabled = true;

    try {
      await this.#save();
      this.#setOpen(false);
    } finally {
      if (this.refs.submit) this.refs.submit.disabled = false;
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === "Escape" && this.classList.contains(classes.open)) {
      event.stopPropagation();
      this.#setOpen(false);
      this.refs.toggle.focus();
    }
  };

  /**
   * @param {boolean} open
   */
  #setOpen(open) {
    const content = this.#content;

    if (open) {
      this.#lockDrawerMaxHeight();
      this.classList.add(classes.open);
      this.refs.panel.hidden = false;
    } else {
      this.classList.remove(classes.open);
      this.refs.panel.hidden = true;
      if (content) content.style.maxHeight = "";
    }

    this.refs.toggle.setAttribute("aria-expanded", String(open));
    this.#syncCoveredContent(open);
    this.#syncToggleLabel();

    if (open) this.refs.input.focus();
  }

  /**
   * Caps the overlay to the remaining drawer height so the form can grow to
   * fit its content without running off-screen.
   */
  #lockDrawerMaxHeight() {
    const content = this.#content;
    const drawer = this.closest(".drawer");
    const footer = this.closest(".drawer-footer");
    if (!content || !drawer || !footer) return;

    const header = drawer.querySelector(".drawer-header");
    const chrome = footer.offsetHeight - content.offsetHeight;
    const available =
      drawer.clientHeight - (header?.offsetHeight ?? 0) - chrome;

    if (available > 0) content.style.maxHeight = `${available}px`;
  }

  /**
   * Keeps covered summary controls out of tab order while the overlay is open.
   * @param {boolean} open
   */
  #syncCoveredContent(open) {
    const content = this.#content;
    if (!content) return;

    for (const child of content.children) {
      if (child === this) continue;
      child.inert = open;
    }
  }

  #syncCount() {
    const remaining = Math.max(0, MAX_LENGTH - this.refs.input.value.length);
    const template =
      this.getAttribute("count-template") || "__COUNT__ characters remaining";

    this.refs.count.textContent = template.replaceAll(
      "__COUNT__",
      String(remaining),
    );
  }

  #syncToggleLabel() {
    const isOpen = this.classList.contains(classes.open);
    const hasNote = this.refs.input.value.trim() !== "";
    const addLabel = this.getAttribute("add-label") || "";
    const editLabel = this.getAttribute("edit-label") || addLabel;
    const closeLabel = this.getAttribute("close-label") || addLabel;

    this.refs.toggleLabel.textContent = isOpen
      ? closeLabel
      : hasNote
        ? editLabel
        : addLabel;
  }

  /**
   * Saves the gift message as the cart/order note.
   * @returns {Promise<void>}
   */
  #save() {
    const note = this.refs.input.value.slice(0, MAX_LENGTH);

    return json(
      fetch(
        `${theme.routes.cart_update_url}.js`,
        fetchConfig("json", {
          body: JSON.stringify({ note }),
        }),
      ),
    ).then(() => undefined);
  }
}

if (!customElements.get("cart-gift-note")) {
  customElements.define("cart-gift-note", CartGiftNote);
}
